/**
 * SixHats — "Popular Hat Upgrades" widget (rope / ball marker / tee holder / scorecard / tee clip)
 *
 * v2.2 (2026-07-04) — preferred default size. On PDP load with no explicit
 *   ?variant= in the URL, auto-select 58cm; if sold out, 56cm; then 60cm.
 *   Never leaves the theme's first-variant default (54cm) selected when a
 *   more common size is in stock. Selection goes through the theme's own
 *   size-popout click handler (changeVariant), so price/stock/UI all update
 *   natively. Explicit ?variant= links (ads, shared links) are honored.
 *
 * v2.1 (2026-07-04) — scope interceptor to the main product form only.
 *   The PDP renders ~64 quick-add mini-forms (one per size) for recommended
 *   products; v2 intercepted those too when an upgrade was active, adding the
 *   main hat (often the 54cm default) instead of the clicked product. The
 *   interceptor now requires data-product-handle on the form, and the hat
 *   variant/quantity are read from the submitted form, not document-wide.
 *
 * v2 (2026-07-03) — event-delegated rewrite.
 *
 * Why: the theme (Pipeline + Alpine) morphs the product section on every variant/size
 * change. The old build bound listeners once on DOMContentLoaded and cached node
 * references in closures, so after a size change the Confirm buttons stayed disabled
 * forever ("can't change ropes / add tee holders"). This build binds ALL events at the
 * document level, looks elements up at click time, keeps state at module level, and
 * re-applies its DOM enhancements after any re-render — so it survives morphs.
 *
 * Also in v2:
 *  - "For hat" line-item property now includes the selected size (resolved from the
 *    product JSON by variant id) so fulfillment knows which hat gets the upgrade.
 *  - While any upgrade is selected, express checkout (PayPal / More payment options)
 *    is hidden so the upgrade can't be silently dropped; it reappears on clear.
 *
 * Deploy: replace Assets/fit-upgrades-logic.js with this file (both stores).
 */
(function () {
  'use strict';

  // Guard: never initialize twice, even if the theme re-executes this script.
  if (window.__fuUpgradesV2) return;
  window.__fuUpgradesV2 = true;

  var CLOUDINARY_CLOUD = 'dsvcreexw';
  var CLOUDINARY_PRESET = 'sixhats_custom_logo';
  var ROPE_HANDLE = 'rope-service';
  var BALL_MARKER_HANDLE = 'magnetic-ball-marker';
  var TEE_HOLDER_CLOUD = 'do8te5umr';
  var TEE_HOLDER_FOLDER = 'tee-holder-composites';

  /* ------------------------------------------------------------------ *
   * State (module-level: survives any DOM re-render)
   * ------------------------------------------------------------------ */

  // Confirmed selections — what gets added to the cart.
  var state = {
    rope: { active: false, action: '', color: '', customColor: '', refImageFile: null, refImageBase64: null, refImageUrl: '' },
    teeHolder: { active: false, styleTitle: '', variantId: null, elastic: '', compositeUrl: '' },
    ballMarker: { active: false },
    scorecard: { active: false, designType: '', color: '', variantId: null, quantity: 0, logoFile: null, logoBase64: null },
    teeClip: { active: false, designType: '', color: '', variantId: null, quantity: 0, logoFile: null, logoBase64: null }
  };

  // In-modal (not yet confirmed) selections.
  var pending = {
    rope: { action: '', color: '', refFile: null, refBase64: null },
    teeHolder: { style: '', variantId: null, elastic: '' },
    scorecard: { designType: '', color: '', variantId: null, qty: 1, file: null, base64: null },
    teeClip: { designType: '', color: '', variantId: null, qty: 1, file: null, base64: null }
  };

  /* ------------------------------------------------------------------ *
   * Small helpers — always query the live DOM, never cache nodes
   * ------------------------------------------------------------------ */

  function qs(sel, root) { return (root || document).querySelector(sel); }
  function qsa(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  function getProductForm() { return qs('form[action*="/cart/add"]'); }

  function getMainHatQuantity(form) {
    // form.elements also finds inputs associated via the form="" attribute.
    var qty = (form && form.elements && form.elements['quantity']) || qs('form[action*="/cart/add"] input[name="quantity"]');
    return qty ? Math.max(1, parseInt(qty.value) || 1) : 1;
  }

  function getMainHatVariantId(form) {
    var input = (form && form.elements && form.elements['id']) || qs('form[action*="/cart/add"] input[name="id"]');
    return input ? input.value : null;
  }

  function anyUpgradeActive() {
    return state.rope.active || state.teeHolder.active || state.ballMarker.active || state.scorecard.active || state.teeClip.active;
  }

  function readFileAsBase64(file) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onload = function (e) { resolve(e.target.result); };
      reader.onerror = function () { reject(new Error('File read failed')); };
      reader.readAsDataURL(file);
    });
  }

  function validateFile(file) {
    var allowed = ['.png', '.jpg', '.jpeg', '.gif', '.svg', '.pdf', '.eps', '.ai'];
    var ext = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (allowed.indexOf(ext) === -1) { alert('Invalid file type. Use PNG, JPG, GIF, SVG, PDF, EPS, or AI.'); return false; }
    if (file.size / (1024 * 1024) > 10) { alert('File too large. Maximum 10MB.'); return false; }
    return true;
  }

  // This Cloudinary account blocks direct .pdf delivery (401), and .eps/.ai don't
  // render in browsers — the order gets an image rendition URL instead of the raw file.
  function viewableUploadUrl(result) {
    var url = result.secure_url || '';
    if (url.indexOf('/image/upload/') === -1) return url;
    var fmt = (result.format || '').toLowerCase();
    if (fmt === 'pdf' || /\.pdf$/i.test(url)) {
      return url.replace('/image/upload/', '/image/upload/pg_1/').replace(/\.pdf$/i, '.jpg');
    }
    if (fmt === 'eps' || fmt === 'ai' || /\.(eps|ai)$/i.test(url)) {
      return url.replace(/\.(eps|ai)$/i, '.jpg');
    }
    return url;
  }

  function uploadToCloudinary(base64Data, fileName) {
    var formData = new FormData();
    formData.append('file', base64Data);
    formData.append('upload_preset', CLOUDINARY_PRESET);
    formData.append('folder', 'product-page-upgrades');
    formData.append('context', 'original_filename=' + fileName + '|upload_date=' + new Date().toISOString());
    formData.append('tags', 'product-page,upgrade-logo');
    return fetch('https://api.cloudinary.com/v1_1/' + CLOUDINARY_CLOUD + '/upload', { method: 'POST', body: formData })
      .then(function (res) {
        if (!res.ok) return res.json().then(function (err) { throw new Error((err.error && err.error.message) || 'Upload failed'); });
        return res.json();
      });
  }

  function openModal(modal) {
    if (!modal) return;
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  function closeModal(modal) {
    if (!modal) return;
    modal.style.display = 'none';
    document.body.style.overflow = '';
  }

  function setSelected(nodes, selectedEl) {
    nodes.forEach(function (n) { n.classList.toggle('is-selected', n === selectedEl); });
  }

  function updateCardBadge(cardKey, text) {
    var card = qs('[data-upgrade-card="' + cardKey + '"]');
    if (!card) return;
    var badge = card.querySelector('[data-card-badge]');
    if (text) {
      if (!badge) {
        badge = document.createElement('div');
        badge.setAttribute('data-card-badge', '');
        badge.className = 'fu-card-badge';
        card.appendChild(badge);
      }
      if (badge.textContent !== text) badge.textContent = text;
      card.classList.add('is-active');
    } else {
      if (badge) badge.remove();
      card.classList.remove('is-active');
    }
  }

  /* ------------------------------------------------------------------ *
   * One-time DOM enhancements — idempotent, re-applied after re-renders
   * ------------------------------------------------------------------ */

  function upgradeRopeModalDOM() {
    var modal = qs('[data-rope-modal]');
    if (!modal) return;

    // Convert the server-rendered "Other" swatch into a White swatch.
    var otherBtn = modal.querySelector('[data-rope-color="Other"]');
    if (otherBtn) {
      otherBtn.setAttribute('data-rope-color', 'White');
      var circle = otherBtn.querySelector('.fu-modal__color-circle');
      if (circle) circle.setAttribute('style', 'background:#ffffff;border:1px solid #d1d5db;');
      Array.prototype.forEach.call(otherBtn.childNodes, function (node) {
        if (node.nodeType === Node.TEXT_NODE) node.textContent = '';
      });
      otherBtn.appendChild(document.createTextNode('White'));
    }

    // Always show the custom-color input with its heading.
    var customWrap = modal.querySelector('[data-rope-custom-color]');
    if (customWrap) customWrap.style.display = 'block';
    var customInput = modal.querySelector('[data-rope-custom-input]');
    if (customInput && customWrap && !customWrap.querySelector('[data-rope-custom-heading]')) {
      var heading = document.createElement('p');
      heading.className = 'fu-modal__section-title';
      heading.setAttribute('data-rope-custom-heading', '');
      heading.textContent = 'Or describe a custom color';
      customWrap.insertBefore(heading, customInput);
      customInput.setAttribute('placeholder', 'e.g. Navy, Coral, Tartan green plaid');
    }

    // Reference-image upload UI.
    var colorSection = modal.querySelector('[data-rope-color-section]');
    if (colorSection && !colorSection.querySelector('[data-rope-ref-wrap]')) {
      var refWrap = document.createElement('div');
      refWrap.setAttribute('data-rope-ref-wrap', '');
      refWrap.innerHTML =
        '<p class="fu-modal__section-title">Upload a reference image (optional)</p>' +
        '<p style="font-size:11px;color:#5f5e5a;margin:0 0 8px;font-style:italic;">Helps our team match the exact rope you\'re looking for.</p>' +
        '<label class="fu-modal__upload" style="display:block;cursor:pointer;padding:16px;border:2px dashed #d1d5db;border-radius:6px;text-align:center;background:#f9fafb;">' +
        '<input type="file" accept="image/*,.pdf,.eps,.ai,.svg" data-rope-ref-upload style="display:none;">' +
        '<span data-rope-ref-label>Click to upload reference image</span></label>';
      colorSection.appendChild(refWrap);
    }
  }

  function addLeadTimeNotice() {
    [qs('[data-rope-modal]'), qs('[data-tee-holder-modal]')].forEach(function (modal) {
      if (!modal || modal.querySelector('[data-leadtime-notice]')) return;
      var confirmBtn = modal.querySelector('[data-rope-confirm], [data-tee-holder-confirm]');
      if (!confirmBtn) return;
      var notice = document.createElement('p');
      notice.setAttribute('data-leadtime-notice', '');
      notice.style.cssText = 'margin:0 0 14px;padding:10px 12px;background:#f5f7fa;border-left:3px solid #185FA5;border-radius:6px;font-size:13px;line-height:1.45;color:#444;';
      notice.innerHTML = '<strong style="color:#185FA5;">Made to order.</strong> Every customization is finished by hand — please allow 24–48 hours to complete your hat upgrade before your order ships.';
      var footer = confirmBtn.closest('.fu-modal__footer');
      if (footer && footer.parentNode) footer.parentNode.insertBefore(notice, footer);
      else confirmBtn.parentNode.insertBefore(notice, confirmBtn);
    });
  }

  /* ------------------------------------------------------------------ *
   * Express checkout (PayPal etc.) bypasses the upgrade interceptor —
   * hide it while an upgrade is selected so upgrades can't be dropped.
   * ------------------------------------------------------------------ */

  function syncExpressCheckout() {
    var hide = anyUpgradeActive();
    qsa('[data-shopify="payment-button"], shopify-accelerated-checkout, .shopify-payment-button').forEach(function (el) {
      el.style.display = hide ? 'none' : '';
    });
  }

  /* ------------------------------------------------------------------ *
   * UI sync — repaint selections/badges/confirm states from module state.
   * Safe to call any time (idempotent); called after every re-render.
   * ------------------------------------------------------------------ */

  function getRopeCustomText() {
    var input = qs('[data-rope-modal] [data-rope-custom-input]');
    return input ? input.value.trim() : '';
  }

  function syncRopeModal() {
    var modal = qs('[data-rope-modal]');
    if (!modal) return;
    var actionBtns = qsa('[data-rope-action]', modal);
    var selectedAction = null;
    actionBtns.forEach(function (b) { if (b.dataset.ropeAction === pending.rope.action) selectedAction = b; });
    setSelected(actionBtns, selectedAction);

    var colorSection = modal.querySelector('[data-rope-color-section]');
    if (colorSection) colorSection.style.display = pending.rope.action && pending.rope.action !== 'remove' ? 'block' : (pending.rope.action === 'remove' ? 'none' : colorSection.style.display);

    var colorBtns = qsa('[data-rope-color]', modal);
    var selectedColor = null;
    colorBtns.forEach(function (b) { if (pending.rope.color && b.dataset.ropeColor === pending.rope.color) selectedColor = b; });
    setSelected(colorBtns, selectedColor);

    var refLabel = modal.querySelector('[data-rope-ref-label]');
    if (refLabel) {
      if (pending.rope.refFile) refLabel.innerHTML = '<span style="color:#16a34a;font-weight:500;">✓ ' + pending.rope.refFile.name + '</span>';
      // (if no pending file, leave server text as-is)
    }

    var confirmBtn = modal.querySelector('[data-rope-confirm]');
    if (confirmBtn) {
      var enabled = false;
      if (pending.rope.action === 'remove') enabled = true;
      else if (pending.rope.action) enabled = pending.rope.color !== '' || getRopeCustomText() !== '';
      confirmBtn.disabled = !enabled;
    }
  }

  function syncTeeHolderModal() {
    var modal = qs('[data-tee-holder-modal]');
    if (!modal) return;
    var elasticBtns = qsa('[data-tee-holder-elastic]', modal);
    var selectedElastic = null;
    elasticBtns.forEach(function (b) { if (b.dataset.teeHolderElastic === pending.teeHolder.elastic) selectedElastic = b; });
    setSelected(elasticBtns, selectedElastic);

    var styleSection = modal.querySelector('[data-tee-holder-style-section]');
    if (styleSection && pending.teeHolder.elastic) styleSection.style.display = 'block';

    var styleCards = qsa('[data-tee-holder-style]', modal);
    var selectedStyle = null;
    styleCards.forEach(function (c) { if (pending.teeHolder.style && c.dataset.teeHolderStyle === pending.teeHolder.style) selectedStyle = c; });
    setSelected(styleCards, selectedStyle);

    var confirmBtn = modal.querySelector('[data-tee-holder-confirm]');
    if (confirmBtn) confirmBtn.disabled = !(pending.teeHolder.style && pending.teeHolder.elastic);
  }

  function syncBadges() {
    // Rope
    if (state.rope.active) {
      var label = state.rope.action.charAt(0).toUpperCase() + state.rope.action.slice(1);
      if (state.rope.color) label += ' • ' + state.rope.color;
      if (state.rope.refImageBase64) label += ' • image attached';
      updateCardBadge('rope', label);
    } else updateCardBadge('rope', '');
    // Ball marker
    updateCardBadge('ball-marker', state.ballMarker.active ? 'Added' : '');
    // Tee holder
    updateCardBadge('tee-holder', state.teeHolder.active ? (state.teeHolder.styleTitle + ' • ' + state.teeHolder.elastic + ' elastic') : '');
    // Scorecard
    updateCardBadge('scorecard', state.scorecard.active ? (state.scorecard.quantity + ' × ' + state.scorecard.color + (state.scorecard.designType === 'custom' ? ' (custom)' : ' (pre-designed)')) : '');
    // Tee clip
    updateCardBadge('tee-clip', state.teeClip.active ? (state.teeClip.quantity + ' × ' + state.teeClip.color + (state.teeClip.designType === 'custom' ? ' (custom)' : ' (pre-designed)')) : '');
  }

  function syncAll() {
    upgradeRopeModalDOM();
    addLeadTimeNotice();
    syncRopeModal();
    syncTeeHolderModal();
    syncBadges();
    syncExpressCheckout();
    applyPreferredDefaultSize();
  }

  /* ------------------------------------------------------------------ *
   * Tee-holder composite image lookup (unchanged logic from v1)
   * ------------------------------------------------------------------ */

  var HAT_SLUG_OVERRIDES = { 'olive & brown suede': ['olive_green', 'olive-green'] };

  function hatSlugVariations(hatName) {
    if (!hatName) return [];
    var trimmed = hatName.toString().toLowerCase().trim();
    var variations = [];
    if (HAT_SLUG_OVERRIDES[trimmed]) HAT_SLUG_OVERRIDES[trimmed].forEach(function (slug) { variations.push(slug); });
    variations.push(trimmed.replace(/&/g, '-').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, ''));
    variations.push(trimmed.replace(/&/g, '_').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, ''));
    if (trimmed.indexOf('plastic') !== -1) {
      var noPlastic = trimmed.replace(/plastic/g, '').replace(/\s+/g, ' ').trim();
      variations.push(noPlastic.replace(/&/g, '_').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, ''));
      variations.push(noPlastic.replace(/&/g, '-').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, ''));
    }
    if (trimmed.indexOf('plastic') !== -1 || trimmed.indexOf('mesh') !== -1) {
      var noPlasticMesh = trimmed.replace(/plastic/g, '').replace(/mesh/g, '').replace(/\s+/g, ' ').trim();
      if (noPlasticMesh) {
        variations.push(noPlasticMesh.replace(/&/g, '_').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, ''));
        variations.push(noPlasticMesh.replace(/&/g, '-').replace(/\s+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, ''));
      }
    }
    if (trimmed.indexOf('&') !== -1) {
      var beforeAmp = trimmed.split('&')[0].trim();
      if (beforeAmp) variations.push(beforeAmp.replace(/\s+/g, '-'));
    }
    return variations.filter(function (v, i, a) { return a.indexOf(v) === i; });
  }

  function materialSlugVariations(materialName) {
    if (!materialName) return [];
    var s = materialName.toString().trim();
    var variations = [];
    var withBorderMatch = s.match(/^(.*?)\s+with\s+(.+?)\s+border$/i);
    if (withBorderMatch) {
      var base = withBorderMatch[1].toLowerCase().replace(/&/g, '').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
      var border = withBorderMatch[2].toLowerCase().replace(/\s+/g, '_');
      variations.push(base + '-_' + border + '_border');
      return variations;
    }
    var fullSlug = s.toLowerCase().replace(/&/g, '').replace(/\s+/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
    variations.push(fullSlug);
    ['_leather', '_patch'].forEach(function (suffix) {
      if (fullSlug.endsWith(suffix)) variations.push(fullSlug.slice(0, -suffix.length));
    });
    if (!fullSlug.endsWith('_patch')) variations.push(fullSlug + '_patch');
    if (!fullSlug.endsWith('_leather')) variations.push(fullSlug + '_leather');
    return variations;
  }

  function buildImageUrlVariations(hatName, materialName, elasticName) {
    var hats = hatSlugVariations(hatName);
    var elastic = (elasticName || '').toString().toLowerCase().trim();
    if (!hats.length || !elastic) return [];
    var materials = materialSlugVariations(materialName);
    var urls = [];
    hats.forEach(function (hat) {
      materials.forEach(function (material) {
        urls.push('https://res.cloudinary.com/' + TEE_HOLDER_CLOUD + '/image/upload/' + TEE_HOLDER_FOLDER + '/' + hat + '-' + material + '-tee_holder-' + elastic + '_elastic.jpg');
      });
    });
    return urls;
  }

  function getHatColor() {
    var el = qs('[data-tee-holder-hat-color]');
    return el ? el.getAttribute('data-tee-holder-hat-color') : '';
  }

  function setImageWithFallback(img, urls, onSuccess) {
    if (!img || !urls || !urls.length) return;
    var i = 0;
    function tryNext() {
      if (i >= urls.length) { img.style.opacity = '0.3'; img.alt = 'Preview not yet available'; return; }
      img.src = urls[i++];
    }
    img.onerror = function () { tryNext(); };
    img.onload = function () { this.style.opacity = '1'; if (onSuccess) onSuccess(this.src); };
    tryNext();
  }

  function updateLeatherImages() {
    var modal = qs('[data-tee-holder-modal]');
    var hatColor = getHatColor();
    if (!modal || !hatColor || !pending.teeHolder.elastic) return;
    qsa('[data-tee-holder-style]', modal).forEach(function (card) {
      var materialName = card.getAttribute('data-tee-holder-style');
      var urls = buildImageUrlVariations(hatColor, materialName, pending.teeHolder.elastic);
      var img = card.querySelector('img');
      var zoomTrigger = card.querySelector('[data-zoomable]');
      setImageWithFallback(img, urls, function (loadedUrl) {
        if (zoomTrigger) zoomTrigger.setAttribute('data-zoom-src', loadedUrl);
      });
    });
  }

  function updateTeeHolderPreview() {
    var modal = qs('[data-tee-holder-modal]');
    if (!modal) return;
    var previewSection = modal.querySelector('[data-tee-holder-preview-section]');
    var previewText = modal.querySelector('[data-tee-holder-preview-text]');
    if (pending.teeHolder.elastic && pending.teeHolder.style && previewText) {
      var productTitle = (document.title.split(' – ')[0].split(' | ')[0] || 'Your hat').trim();
      previewText.innerHTML = '<strong>' + productTitle + '</strong><br>' + pending.teeHolder.style + ' + ' + pending.teeHolder.elastic + ' elastic';
      var previewImg = modal.querySelector('[data-tee-holder-preview-img]');
      if (previewImg) {
        var urls = buildImageUrlVariations(getHatColor(), pending.teeHolder.style, pending.teeHolder.elastic);
        if (urls.length) {
          previewImg.alt = productTitle + ' with ' + pending.teeHolder.style + ' + ' + pending.teeHolder.elastic + ' elastic';
          setImageWithFallback(previewImg, urls);
        }
      }
      if (previewSection) previewSection.style.display = 'block';
    } else if (previewSection) previewSection.style.display = 'none';
  }

  /* ------------------------------------------------------------------ *
   * Delegated CLICK handling
   * ------------------------------------------------------------------ */

  document.addEventListener('click', function (e) {
    var t = e.target;

    // --- Modal close / backdrop ---
    var closer = t.closest('[data-modal-close], [data-modal-backdrop]');
    if (closer) {
      var modalToClose = closer.closest('.fu-modal');
      if (modalToClose) { closeModal(modalToClose); return; }
    }

    // --- Upgrade cards (open modals / toggle ball marker) ---
    var card = t.closest('[data-upgrade-card]');
    if (card && !t.closest('.fu-modal')) {
      var key = card.getAttribute('data-upgrade-card');
      if (key === 'rope') {
        var ropeModal = qs('[data-rope-modal]');
        upgradeRopeModalDOM();
        addLeadTimeNotice();
        openModal(ropeModal);
        // Products without a rope offer an "Add rope" action — auto-select it.
        if (!pending.rope.action && ropeModal) {
          var addBtn = ropeModal.querySelector('[data-rope-action="add"]');
          if (addBtn) { pending.rope.action = 'add'; }
        }
        syncRopeModal();
        return;
      }
      if (key === 'ball-marker') {
        state.ballMarker.active = !state.ballMarker.active;
        syncBadges();
        syncExpressCheckout();
        return;
      }
      if (key === 'tee-holder') {
        var teeModal = qs('[data-tee-holder-modal]');
        addLeadTimeNotice();
        openModal(teeModal);
        if (!pending.teeHolder.elastic) {
          pending.teeHolder.elastic = 'Black';
          updateLeatherImages();
        }
        syncTeeHolderModal();
        updateTeeHolderPreview();
        return;
      }
      if (key === 'scorecard') { openModal(qs('[data-scorecard-modal]')); return; }
      if (key === 'tee-clip') { openModal(qs('[data-tee-clip-modal]')); return; }
    }

    // --- Rope modal ---
    var ropeAction = t.closest('[data-rope-action]');
    if (ropeAction) {
      pending.rope.action = ropeAction.dataset.ropeAction;
      syncRopeModal();
      return;
    }
    var ropeColor = t.closest('[data-rope-color]');
    if (ropeColor) {
      pending.rope.color = ropeColor.dataset.ropeColor;
      var ci = qs('[data-rope-modal] [data-rope-custom-input]');
      if (ci && ci.value.trim() !== '') ci.value = '';
      syncRopeModal();
      return;
    }
    if (t.closest('[data-rope-confirm]')) {
      var confirmBtn = t.closest('[data-rope-confirm]');
      if (confirmBtn.disabled) return;
      state.rope.active = true;
      state.rope.action = pending.rope.action;
      var customText = getRopeCustomText();
      if (pending.rope.action === 'remove') {
        state.rope.color = '';
        state.rope.customColor = '';
      } else if (customText) {
        state.rope.color = customText;
        state.rope.customColor = customText;
      } else {
        state.rope.color = pending.rope.color;
        state.rope.customColor = '';
      }
      state.rope.refImageFile = pending.rope.refFile;
      state.rope.refImageBase64 = pending.rope.refBase64;
      state.rope.refImageUrl = '';
      syncBadges();
      syncExpressCheckout();
      closeModal(qs('[data-rope-modal]'));
      return;
    }
    if (t.closest('[data-rope-clear]')) {
      state.rope = { active: false, action: '', color: '', customColor: '', refImageFile: null, refImageBase64: null, refImageUrl: '' };
      pending.rope = { action: '', color: '', refFile: null, refBase64: null };
      var rm = qs('[data-rope-modal]');
      if (rm) {
        qsa('.is-selected', rm).forEach(function (el) { el.classList.remove('is-selected'); });
        var input = rm.querySelector('[data-rope-custom-input]'); if (input) input.value = '';
        var upload = rm.querySelector('[data-rope-ref-upload]'); if (upload) upload.value = '';
        var refLabel = rm.querySelector('[data-rope-ref-label]'); if (refLabel) refLabel.textContent = 'Click to upload reference image';
        var cs = rm.querySelector('[data-rope-color-section]'); if (cs) cs.style.display = 'none';
        var cb = rm.querySelector('[data-rope-confirm]'); if (cb) cb.disabled = true;
      }
      syncBadges();
      syncExpressCheckout();
      closeModal(rm);
      return;
    }

    // --- Tee holder modal ---
    var elasticBtn = t.closest('[data-tee-holder-elastic]');
    if (elasticBtn) {
      pending.teeHolder.elastic = elasticBtn.dataset.teeHolderElastic;
      updateLeatherImages();
      syncTeeHolderModal();
      updateTeeHolderPreview();
      return;
    }
    var styleCard = t.closest('[data-tee-holder-style]');
    if (styleCard && !t.closest('[data-zoomable]')) {
      pending.teeHolder.style = styleCard.dataset.teeHolderStyle;
      pending.teeHolder.variantId = styleCard.dataset.teeHolderVariantId;
      syncTeeHolderModal();
      updateTeeHolderPreview();
      return;
    }
    if (t.closest('[data-tee-holder-confirm]')) {
      var teeConfirm = t.closest('[data-tee-holder-confirm]');
      if (teeConfirm.disabled) return;
      var compositeUrl = '';
      var tm = qs('[data-tee-holder-modal]');
      var selectedCard = tm && tm.querySelector('[data-tee-holder-style].is-selected');
      if (selectedCard) {
        var img = selectedCard.querySelector('img');
        if (img && img.src && !img.src.endsWith('placeholder.jpg') && img.naturalWidth > 0) compositeUrl = img.src;
      }
      state.teeHolder = {
        active: true,
        styleTitle: pending.teeHolder.style,
        variantId: pending.teeHolder.variantId ? parseInt(pending.teeHolder.variantId, 10) : null,
        elastic: pending.teeHolder.elastic,
        compositeUrl: compositeUrl
      };
      syncBadges();
      syncExpressCheckout();
      closeModal(tm);
      return;
    }
    if (t.closest('[data-tee-holder-clear]')) {
      state.teeHolder = { active: false, styleTitle: '', variantId: null, elastic: '', compositeUrl: '' };
      pending.teeHolder = { style: '', variantId: null, elastic: '' };
      var thm = qs('[data-tee-holder-modal]');
      if (thm) {
        qsa('.is-selected', thm).forEach(function (el) { el.classList.remove('is-selected'); });
        var ss = thm.querySelector('[data-tee-holder-style-section]'); if (ss) ss.style.display = 'none';
        var ps = thm.querySelector('[data-tee-holder-preview-section]'); if (ps) ps.style.display = 'none';
        var tcb = thm.querySelector('[data-tee-holder-confirm]'); if (tcb) tcb.disabled = true;
      }
      syncBadges();
      syncExpressCheckout();
      closeModal(thm);
      return;
    }

    // --- Scorecard + Tee clip modals (shared handler) ---
    handleAccessoryClick(e, t, 'scorecard', {
      modal: '[data-scorecard-modal]', type: 'data-scorecard-type', typeKey: 'scorecardType',
      variant: '[data-scorecard-variant]', variantAttr: 'scorecardVariant', colorAttr: 'simpleColor', variantIdAttr: 'variantId',
      options: '[data-scorecard-options-section]', upload: '[data-scorecard-upload-section]',
      qtyInput: '[data-scorecard-qty-input]', qtyDec: '[data-scorecard-qty-dec]', qtyInc: '[data-scorecard-qty-inc]',
      uploadInput: '[data-scorecard-upload]', uploadLabel: '[data-scorecard-upload-label]',
      confirm: '[data-scorecard-confirm]', clear: '[data-scorecard-clear]'
    });
    handleAccessoryClick(e, t, 'teeClip', {
      modal: '[data-tee-clip-modal]', type: 'data-teeclip-type', typeKey: 'teeclipType',
      variant: '[data-teeclip-variant]', variantAttr: 'teeclipVariant', colorAttr: 'teeclipColor', variantIdAttr: 'teeclipVariantId',
      options: '[data-teeclip-options-section]', upload: '[data-teeclip-upload-section]',
      qtyInput: '[data-teeclip-qty-input]', qtyDec: '[data-teeclip-qty-dec]', qtyInc: '[data-teeclip-qty-inc]',
      uploadInput: '[data-teeclip-upload]', uploadLabel: '[data-teeclip-upload-label]',
      confirm: '[data-teeclip-confirm]', clear: '[data-teeclip-clear]'
    });
  });

  function accessoryConfirmState(key) {
    var p = pending[key];
    if (!p.designType || !p.color || p.qty < 1) return true; // disabled
    if (p.designType === 'custom' && !p.file) return true;
    return false;
  }

  function syncAccessoryModal(key, cfg) {
    var modal = qs(cfg.modal);
    if (!modal) return;
    var p = pending[key];
    var typeBtns = qsa('[' + cfg.type + ']', modal);
    var selType = null;
    typeBtns.forEach(function (b) { if (b.dataset[cfg.typeKey] === p.designType) selType = b; });
    setSelected(typeBtns, selType);
    qsa(cfg.variant, modal).forEach(function (v) {
      v.style.display = v.dataset[cfg.variantAttr] === p.designType ? '' : 'none';
      v.classList.toggle('is-selected', !!p.color && v.dataset[cfg.colorAttr] === p.color && v.dataset[cfg.variantAttr] === p.designType);
    });
    var opts = modal.querySelector(cfg.options); if (opts) opts.style.display = p.designType ? 'block' : 'none';
    var up = modal.querySelector(cfg.upload); if (up) up.style.display = p.designType === 'custom' ? 'block' : 'none';
    var qty = modal.querySelector(cfg.qtyInput); if (qty) qty.value = p.qty;
    var confirmBtn = modal.querySelector(cfg.confirm); if (confirmBtn) confirmBtn.disabled = accessoryConfirmState(key);
  }

  function handleAccessoryClick(e, t, key, cfg) {
    var modal = t.closest(cfg.modal);
    if (!modal) return;
    var p = pending[key];

    var typeBtn = t.closest('[' + cfg.type + ']');
    if (typeBtn) {
      p.designType = typeBtn.dataset[cfg.typeKey];
      p.color = '';
      p.variantId = null;
      if (p.designType === 'predesigned') {
        p.file = null; p.base64 = null;
        var ui = modal.querySelector(cfg.uploadInput); if (ui) ui.value = '';
        var ul = modal.querySelector(cfg.uploadLabel); if (ul) ul.textContent = 'Click to upload';
      }
      syncAccessoryModal(key, cfg);
      return;
    }
    var variantCard = t.closest(cfg.variant);
    if (variantCard) {
      if (variantCard.dataset[cfg.variantAttr] === p.designType) {
        p.color = variantCard.dataset[cfg.colorAttr];
        p.variantId = variantCard.dataset[cfg.variantIdAttr];
        syncAccessoryModal(key, cfg);
      }
      return;
    }
    if (t.closest(cfg.qtyDec)) { p.qty = Math.max(1, p.qty - 1); syncAccessoryModal(key, cfg); return; }
    if (t.closest(cfg.qtyInc)) { p.qty = Math.max(1, p.qty + 1); syncAccessoryModal(key, cfg); return; }
    if (t.closest(cfg.confirm)) {
      if (t.closest(cfg.confirm).disabled) return;
      state[key] = {
        active: true,
        designType: p.designType,
        color: p.color,
        variantId: p.variantId ? parseInt(p.variantId, 10) : null,
        quantity: p.qty,
        logoFile: p.file,
        logoBase64: p.base64
      };
      syncBadges();
      syncExpressCheckout();
      closeModal(modal);
      return;
    }
    if (t.closest(cfg.clear)) {
      state[key] = { active: false, designType: '', color: '', variantId: null, quantity: 0, logoFile: null, logoBase64: null };
      pending[key] = { designType: '', color: '', variantId: null, qty: 1, file: null, base64: null };
      qsa('.is-selected', modal).forEach(function (el) { el.classList.remove('is-selected'); });
      var ui2 = modal.querySelector(cfg.uploadInput); if (ui2) ui2.value = '';
      var ul2 = modal.querySelector(cfg.uploadLabel); if (ul2) ul2.textContent = 'Click to upload';
      syncAccessoryModal(key, cfg);
      syncBadges();
      syncExpressCheckout();
      closeModal(modal);
      return;
    }
  }

  /* ------------------------------------------------------------------ *
   * Delegated INPUT / CHANGE handling
   * ------------------------------------------------------------------ */

  document.addEventListener('input', function (e) {
    var t = e.target;
    if (t.matches('[data-rope-custom-input]')) {
      if (t.value.trim() !== '') pending.rope.color = '';
      syncRopeModal();
      return;
    }
    if (t.matches('[data-scorecard-qty-input]')) {
      pending.scorecard.qty = Math.max(1, parseInt(t.value) || 1);
      var scb = qs('[data-scorecard-modal] [data-scorecard-confirm]');
      if (scb) scb.disabled = accessoryConfirmState('scorecard');
      return;
    }
    if (t.matches('[data-teeclip-qty-input]')) {
      pending.teeClip.qty = Math.max(1, parseInt(t.value) || 1);
      var tcb = qs('[data-tee-clip-modal] [data-teeclip-confirm]');
      if (tcb) tcb.disabled = accessoryConfirmState('teeClip');
      return;
    }
  });

  document.addEventListener('change', function (e) {
    var t = e.target;

    if (t.matches('[data-rope-ref-upload]')) {
      var file = t.files[0];
      var refLabel = qs('[data-rope-modal] [data-rope-ref-label]');
      if (!file) {
        pending.rope.refFile = null; pending.rope.refBase64 = null;
        if (refLabel) refLabel.textContent = 'Click to upload reference image';
        return;
      }
      if (!validateFile(file)) { t.value = ''; return; }
      pending.rope.refFile = file;
      readFileAsBase64(file).then(function (b64) {
        pending.rope.refBase64 = b64;
        if (refLabel) refLabel.innerHTML = '<span style="color:#16a34a;font-weight:500;">✓ ' + file.name + '</span>';
      });
      return;
    }

    function handleAccessoryUpload(key, labelSel, confirmSel) {
      var f = t.files[0];
      if (!f || !validateFile(f)) { t.value = ''; return; }
      pending[key].file = f;
      readFileAsBase64(f).then(function (b64) {
        pending[key].base64 = b64;
        var label = qs(labelSel);
        if (label) label.innerHTML = '<span style="color:#16a34a;font-weight:500;">✓ ' + f.name + '</span>';
        var cb = qs(confirmSel);
        if (cb) cb.disabled = accessoryConfirmState(key);
      });
    }
    if (t.matches('[data-scorecard-upload]')) { handleAccessoryUpload('scorecard', '[data-scorecard-modal] [data-scorecard-upload-label]', '[data-scorecard-modal] [data-scorecard-confirm]'); return; }
    if (t.matches('[data-teeclip-upload]')) { handleAccessoryUpload('teeClip', '[data-tee-clip-modal] [data-teeclip-upload-label]', '[data-tee-clip-modal] [data-teeclip-confirm]'); return; }
  });

  /* ------------------------------------------------------------------ *
   * Add-to-cart interception (delegated, capture phase so it runs before
   * the theme's own AJAX submit handler)
   * ------------------------------------------------------------------ */

  function getSelectedSizeLabel(hatVariantId, form) {
    // Preferred: resolve the variant title from the product JSON — reliable
    // regardless of what size-picker UI the theme renders.
    var handle = form.getAttribute('data-product-handle');
    var jsonLookup = handle
      ? fetch('/products/' + handle + '.js')
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (product) {
            if (!product) return '';
            var v = product.variants.find(function (v) { return String(v.id) === String(hatVariantId); });
            return v ? v.title : '';
          })
          .catch(function () { return ''; })
      : Promise.resolve('');
    return jsonLookup.then(function (size) {
      if (size) return size;
      // Fallback: old selector-based approach.
      var el = qs('.size-picker__option.is-selected .size-picker__name, fieldset.product-form__input input:checked, select[name^="options"] option:checked');
      if (el) return ((el.value || el.textContent) || '').trim();
      return '';
    });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.matches || !form.matches('form[action*="/cart/add"]')) return;
    // Only intercept the MAIN product form. The PDP also renders dozens of
    // quick-add mini-forms (one per size) for recommended products — those
    // must never be hijacked, or we'd add the main hat instead of the
    // product the customer clicked.
    if (!form.hasAttribute('data-product-handle')) return;
    if (!anyUpgradeActive()) return; // no upgrades — let the theme handle it normally

    e.preventDefault();
    e.stopImmediatePropagation();
    e.stopPropagation();

    var submitBtn = form.querySelector('[type="submit"]');
    var originalHTML = submitBtn ? submitBtn.innerHTML : '';
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Processing...'; }

    (async function () {
      try {
        var hatVariantId = getMainHatVariantId(form);
        var hatQty = getMainHatQuantity(form);
        if (!hatVariantId) throw new Error('Hat variant not found');

        var productTitleEl = qs('.product__title, h1.product-title, h1[class*="product"]');
        var productTitle = productTitleEl
          ? productTitleEl.textContent.trim()
          : document.title.split(' – ')[0].split(' | ')[0].trim();

        var selectedSize = await getSelectedSizeLabel(hatVariantId, form);
        var forHatLabel = selectedSize ? productTitle + ' — ' + selectedSize : productTitle;

        var items = [];
        var cartAttributes = {};
        var cartNoteParts = [];

        items.push({ id: parseInt(hatVariantId, 10), quantity: hatQty });

        // Rope
        if (state.rope.active) {
          var ropeRefUrl = '';
          if (state.rope.refImageBase64 && state.rope.refImageFile) {
            if (submitBtn) submitBtn.textContent = 'Uploading rope reference...';
            ropeRefUrl = viewableUploadUrl(await uploadToCloudinary(state.rope.refImageBase64, state.rope.refImageFile.name));
            state.rope.refImageUrl = ropeRefUrl;
            cartAttributes.Rope_Reference_Image_URL = ropeRefUrl;
            cartNoteParts.push('Rope reference image: ' + ropeRefUrl);
            if (submitBtn) submitBtn.textContent = 'Processing...';
          }
          var props = {
            'For hat': forHatLabel,
            'Action': state.rope.action.charAt(0).toUpperCase() + state.rope.action.slice(1)
          };
          if (state.rope.color) props['Rope Color'] = state.rope.color;
          if (state.rope.customColor) props['Custom color description'] = state.rope.customColor;
          if (ropeRefUrl) props['Rope Reference Image'] = ropeRefUrl;

          var ropeRes = await fetch('/products/' + ROPE_HANDLE + '.js');
          if (!ropeRes.ok) throw new Error('Could not fetch rope product');
          var ropeData = await ropeRes.json();
          var targetAction = state.rope.action.toLowerCase();
          var matchingVariant = ropeData.variants.find(function (v) { return v.title.toLowerCase().includes(targetAction); });
          if (!matchingVariant) throw new Error('No rope variant found matching action: ' + targetAction);
          items.push({ id: matchingVariant.id, quantity: hatQty, properties: props });
        }

        // Ball marker
        if (state.ballMarker.active) {
          items.push({ handle: BALL_MARKER_HANDLE, quantity: hatQty, properties: { 'For hat': forHatLabel } });
        }

        // Tee holder
        if (state.teeHolder.active && state.teeHolder.variantId) {
          var teeProps = { 'For hat': forHatLabel, 'Style': state.teeHolder.styleTitle, 'Elastic': state.teeHolder.elastic };
          if (state.teeHolder.compositeUrl) teeProps['Composite Image'] = state.teeHolder.compositeUrl;
          items.push({ id: state.teeHolder.variantId, quantity: hatQty, properties: teeProps });
        }

        // Scorecard
        if (state.scorecard.active && state.scorecard.variantId) {
          var scProps = { 'For hat': forHatLabel, 'Color': state.scorecard.color, 'Design': state.scorecard.designType === 'custom' ? 'Custom' : 'Pre-designed' };
          if (state.scorecard.designType === 'custom' && state.scorecard.logoBase64) {
            var scUpload = await uploadToCloudinary(state.scorecard.logoBase64, state.scorecard.logoFile.name);
            var scLogoUrl = viewableUploadUrl(scUpload);
            scProps.Logo = scLogoUrl;
            cartAttributes.Scorecard_Logo_URL = scLogoUrl;
            cartNoteParts.push('Scorecard book logo: ' + scLogoUrl);
          }
          items.push({ id: state.scorecard.variantId, quantity: state.scorecard.quantity, properties: scProps });
        }

        // Tee clip
        if (state.teeClip.active && state.teeClip.variantId) {
          var tcProps = { 'For hat': forHatLabel, 'Color': state.teeClip.color, 'Design': state.teeClip.designType === 'custom' ? 'Custom' : 'Pre-designed' };
          if (state.teeClip.designType === 'custom' && state.teeClip.logoBase64) {
            var tcUpload = await uploadToCloudinary(state.teeClip.logoBase64, state.teeClip.logoFile.name);
            var tcLogoUrl = viewableUploadUrl(tcUpload);
            tcProps.Logo = tcLogoUrl;
            cartAttributes.TeeClip_Logo_URL = tcLogoUrl;
            cartNoteParts.push('Tee clip logo: ' + tcLogoUrl);
          }
          items.push({ id: state.teeClip.variantId, quantity: state.teeClip.quantity, properties: tcProps });
        }

        // Resolve handle-based items to a variant id.
        for (var i = 0; i < items.length; i++) {
          var item = items[i];
          if (item.handle && !item.id) {
            var productRes = await fetch('/products/' + item.handle + '.js');
            if (!productRes.ok) throw new Error('Could not fetch upgrade product: ' + item.handle);
            var productData = await productRes.json();
            var firstAvailable = productData.variants.find(function (v) { return v.available; }) || productData.variants[0];
            item.id = firstAvailable.id;
            delete item.handle;
          }
        }

        items.reverse();
        var addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: items })
        });
        if (!addRes.ok) {
          var errData = await addRes.json();
          throw new Error(errData.description || 'Failed to add to cart');
        }

        if (Object.keys(cartAttributes).length || cartNoteParts.length) {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: cartAttributes, note: cartNoteParts.join('\n') })
          });
        }

        window.location.href = '/cart';
      } catch (err) {
        console.error('Add to cart error:', err);
        alert('Error adding to cart: ' + err.message);
        if (submitBtn) { submitBtn.disabled = false; submitBtn.innerHTML = originalHTML; }
      }
    })();
  }, true);

  /* ------------------------------------------------------------------ *
   * Preferred default size (v2.2)
   *
   * The theme pre-selects the FIRST variant on page load, which on the
   * Low Pro size run (54–60cm) means 54cm — our least common size — and
   * every no-touch add-to-cart lands a 54cm hat. Instead, default to the
   * most common sizes: 58cm, else 56cm, else 60cm. If none of those are
   * purchasable, leave the theme's default alone.
   * ------------------------------------------------------------------ */

  var SIZE_PREFERENCE = [/\b58\s*cm/i, /\b56\s*cm/i, /\b60\s*cm/i];
  var sizeDefaultApplied = false;

  function applyPreferredDefaultSize() {
    if (sizeDefaultApplied) return;
    // Explicit variant links (ads, shared URLs, back-button restores) win.
    if (/[?&]variant=/.test(window.location.search)) { sizeDefaultApplied = true; return; }
    var form = qs('form[action*="/cart/add"][data-product-handle]');
    if (!form) return; // PDP section not rendered yet — retried on next sync
    var handle = form.getAttribute('data-product-handle');
    var opts = qsa('a[data-popout-option][href*="/products/' + handle + '?variant="]');
    if (!opts.length) { sizeDefaultApplied = true; return; } // no size popout (single-size product)
    var target = null;
    for (var i = 0; i < SIZE_PREFERENCE.length && !target; i++) {
      for (var j = 0; j < opts.length && !target; j++) {
        var label = opts[j].getAttribute('data-value') || '';
        if (SIZE_PREFERENCE[i].test(label) && !opts[j].classList.contains('sold-out')) target = opts[j];
      }
    }
    if (!target) { sizeDefaultApplied = true; return; } // only 54cm (or nothing) in stock — theme default stands
    var li = target.closest('.select-popout__item');
    if (li && li.classList.contains('select-popout__item--current')) { sizeDefaultApplied = true; return; } // already selected
    // Only click once the theme's Alpine component is initialized (its
    // @click.prevent handler is what performs the variant change). Before
    // full page load, keep retrying; after it, give up gracefully.
    var alpineRoot = target.closest('[x-data]');
    var alpineReady = alpineRoot && alpineRoot._x_dataStack;
    if (!alpineReady && document.readyState !== 'complete') return; // retry on next sync/timer
    sizeDefaultApplied = true;
    if (!alpineReady) return; // Alpine never initialized — do nothing rather than misfire
    // Guard: if the theme's @click.prevent handler isn't attached for any
    // reason, make sure the anchor click can't cause a full page navigation.
    var noNav = function (e) { e.preventDefault(); };
    target.addEventListener('click', noNav);
    target.click(); // theme-native changeVariant() → UI, price, form input all sync
    target.removeEventListener('click', noNav);
  }

  /* ------------------------------------------------------------------ *
   * Survive theme re-renders (Alpine morph on variant change):
   * whenever the DOM changes, re-apply enhancements + repaint UI state.
   * All sync functions are idempotent, so this settles immediately.
   * ------------------------------------------------------------------ */

  var syncTimer = null;
  var observer = new MutationObserver(function () {
    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(syncAll, 150);
  });

  function init() {
    syncAll();
    observer.observe(document.body, { childList: true, subtree: true });
    // The size-default depends on Alpine being initialized, which can land
    // after DOMContentLoaded without further DOM mutations — retry on a
    // short timer ladder until it applies (each call is a cheap no-op once
    // sizeDefaultApplied is set).
    [100, 400, 1000, 2500].forEach(function (ms) {
      setTimeout(applyPreferredDefaultSize, ms);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
